/**
 * Offline help (D10-UI-004, D10-API-004).
 *
 * Shown instead of the chat when the service is off-line (no provider, budget
 * spent, every stream slot busy, or the kill switch flipped mid-session). It
 * searches the same generated help corpus locally, lists matching pages and
 * tours from the user's own allowed lists, and keeps the tour and feedback
 * quick actions -- so the bubble is never a dead end. A page guide's article
 * drops its raw "Page address" line and links the page instead, when it is one
 * of the user's own pages (#4599). The corpus is fetched on
 * first use from `GET /api/v1/support/kb/`, never bundled into the SPA.
 */
import { useEffect, useId, useMemo, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { ArrowRight, ChevronDown, Compass, Loader2, RefreshCw, Search } from "lucide-react";
import { getSupportKb, type SupportKb } from "../../api/support";
import type { OfflineReason } from "../../api/support";
import { articleView, searchHelp } from "./searchHelp";

// The corpus is the same for every user and changes only on deploy: fetch once per tab.
let kbPromise: Promise<SupportKb> | null = null;
function loadKb(): Promise<SupportKb> {
  if (!kbPromise) {
    kbPromise = getSupportKb().catch((err) => {
      kbPromise = null;
      throw err;
    });
  }
  return kbPromise;
}

const REASON_COPY: Record<OfflineReason | "status", string> = {
  busy: "The chat is busy right now.",
  budget: "The chat is resting for now.",
  unavailable: "The chat isn't available right now.",
  disabled: "The chat isn't available right now.",
  status: "The chat isn't available right now.",
};

interface Props {
  reason: OfflineReason | "status";
  initialQuery: string;
  allowedRoutes: readonly string[];
  allowedTours: readonly string[];
  onStartTour: (tourId: string) => void;
  onNavigate: () => void;
  onRetryChat?: () => void;
}

export function OfflineHelp({
  reason,
  initialQuery,
  allowedRoutes,
  allowedTours,
  onStartTour,
  onNavigate,
  onRetryChat,
}: Props) {
  const { pathname } = useLocation();
  const [kb, setKb] = useState<SupportKb | null>(null);
  const [failed, setFailed] = useState(false);
  const [query, setQuery] = useState(initialQuery);
  const [expanded, setExpanded] = useState<string | null>(null);
  const inputId = useId();

  useEffect(() => {
    let live = true;
    loadKb()
      .then((data) => live && setKb(data))
      .catch(() => live && setFailed(true));
    return () => {
      live = false;
    };
  }, []);

  const results = useMemo(
    () => (kb ? searchHelp(kb, query, allowedRoutes, allowedTours) : null),
    [kb, query, allowedRoutes, allowedTours],
  );
  const nothing =
    results && query.trim() && !results.sections.length && !results.pages.length && !results.tours.length;

  return (
    <div className="flex flex-col gap-4">
      <div className="rounded-lg border border-[var(--color-border)] border-l-[3px] border-l-[var(--color-accent)] bg-[var(--color-bg-page)] px-3 py-2.5 text-sm text-[var(--color-text-secondary)]">
        <p>
          {REASON_COPY[reason]} You can still search the help guides, take a tour or report a
          problem.
        </p>
        {onRetryChat && (
          <button
            type="button"
            onClick={onRetryChat}
            className="mt-2 inline-flex min-h-[44px] items-center gap-2 rounded-lg px-2 text-sm font-medium text-[var(--color-accent)] hover:bg-[var(--color-bg-hover)]"
          >
            <RefreshCw className="h-4 w-4" aria-hidden="true" />
            Try the chat again
          </button>
        )}
      </div>

      <div>
        <label htmlFor={inputId} className="sr-only">
          Search help
        </label>
        <div className="relative">
          <Search
            className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[var(--color-text-muted)]"
            aria-hidden="true"
          />
          <input
            id={inputId}
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search help, e.g. add a listing"
            className="w-full min-h-[44px] rounded-lg border border-[var(--color-border)] bg-[var(--color-bg-input)] py-2 pl-9 pr-3 text-base text-[var(--color-text-primary)] placeholder:text-[var(--color-text-muted)] focus:border-[var(--color-accent)] focus:outline-hidden"
          />
        </div>
      </div>

      {!kb && !failed && (
        <p className="flex items-center gap-2 text-sm text-[var(--color-text-muted)]">
          <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
          Loading help…
        </p>
      )}
      {failed && (
        <p className="text-sm text-[var(--color-text-muted)]">
          Help search couldn't load. You can still take a tour or report a problem below.
        </p>
      )}

      {results && (
        <div aria-live="polite" className="flex flex-col gap-4">
          {nothing && (
            <p className="text-sm text-[var(--color-text-muted)]">
              Nothing matched. Try other words, or report a problem below.
            </p>
          )}

          {results.pages.length > 0 && (
            <section>
              <h3 className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-[var(--color-text-muted)]">
                Pages
              </h3>
              <ul className="flex flex-col gap-1">
                {results.pages.map((p) => (
                  <li key={p.route}>
                    <Link
                      to={p.route}
                      onClick={onNavigate}
                      className="flex min-h-[44px] items-center justify-between gap-3 rounded-lg px-3 py-2 hover:bg-[var(--color-bg-hover)]"
                    >
                      <span className="min-w-0">
                        <span className="block text-sm font-medium text-[var(--color-text-primary)]">
                          {p.label}
                        </span>
                        <span className="block truncate text-xs text-[var(--color-text-muted)]">
                          {p.purpose}
                        </span>
                      </span>
                      <ArrowRight className="h-4 w-4 shrink-0 text-[var(--color-text-muted)]" aria-hidden="true" />
                    </Link>
                  </li>
                ))}
              </ul>
            </section>
          )}

          {results.tours.length > 0 && (
            <section>
              <h3 className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-[var(--color-text-muted)]">
                Tours
              </h3>
              <ul className="flex flex-col gap-1">
                {results.tours.map((t) => (
                  <li key={t.id}>
                    <button
                      type="button"
                      onClick={() => onStartTour(t.id)}
                      className="flex w-full min-h-[44px] items-center gap-3 rounded-lg px-3 py-2 text-left text-sm font-medium text-[var(--color-text-primary)] hover:bg-[var(--color-bg-hover)]"
                    >
                      <Compass className="h-4 w-4 shrink-0 text-[var(--color-accent)]" aria-hidden="true" />
                      {t.title}
                    </button>
                  </li>
                ))}
              </ul>
            </section>
          )}

          {results.sections.length > 0 && (
            <section>
              <h3 className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-[var(--color-text-muted)]">
                Help articles
              </h3>
              <ul className="flex flex-col gap-1">
                {results.sections.map((s) => {
                  const open = expanded === s.id;
                  const view = open ? articleView(s, kb?.pages ?? [], allowedRoutes, pathname) : null;
                  return (
                    <li key={s.id} className="rounded-lg border border-[var(--color-border)]">
                      <button
                        type="button"
                        aria-expanded={open}
                        onClick={() => setExpanded(open ? null : s.id)}
                        className="flex w-full min-h-[44px] items-center justify-between gap-3 px-3 py-2 text-left text-sm font-medium text-[var(--color-text-primary)]"
                      >
                        {s.title}
                        <ChevronDown
                          className={`h-4 w-4 shrink-0 text-[var(--color-text-muted)] transition-transform ${open ? "rotate-180" : ""}`}
                          aria-hidden="true"
                        />
                      </button>
                      {view && (
                        <div className="border-t border-[var(--color-border)] px-3 py-2 text-sm text-[var(--color-text-secondary)]">
                          <p className="whitespace-pre-line">{view.text}</p>
                          {view.page && (
                            <Link
                              to={view.page.route}
                              onClick={onNavigate}
                              data-testid="offline-article-page-link"
                              className="mt-2 inline-flex min-h-[44px] items-center gap-1.5 font-medium text-[var(--color-accent)] underline underline-offset-2 hover:text-[var(--color-accent-hover)]"
                            >
                              Open {view.page.label}
                              <ArrowRight className="h-4 w-4" aria-hidden="true" />
                            </Link>
                          )}
                        </div>
                      )}
                    </li>
                  );
                })}
              </ul>
            </section>
          )}
        </div>
      )}
    </div>
  );
}
